import { Link } from 'react-router-dom';
import type { SolProjectSourceCard } from '../../data/sol/sol-contract';
import type { ProjectStatus } from '../../data/database.types';
import { PROJECT_STATUS_LABEL } from '../projects/status';
import { formatInstant } from '../../lib/timezone';
import { projectLabel } from './sol-wording';

/**
 * One cited PROJECT, as a real link to that Project. M11 — the Project
 * grounding fix.
 *
 * **IT EXISTS BECAUSE A PROJECT ANSWER USED TO HAVE NOTHING TO STAND ON.** Sol
 * could look a Project up, but the facts arrived uncitable — so *"what is the
 * status of this Project?"* could only be answered without a citation, which
 * Crewlink's own contract refuses, or as general help, which would be general
 * knowledge dressed as a business record. Both were observed on the real
 * provider. A Project is a Crewlink record, and a claim about one now shows the
 * record it rests on, exactly as a Capture claim does.
 *
 * **THE SAME RULES AS THE CAPTURE CARD, DELIBERATELY.** It navigates with the
 * card's own Project id, never the route's · it is a real `Link`, not a tappable
 * `div` · **no UUID is rendered and none is in the accessible name** · and the
 * card is the server's, unaltered — Crewlink does not summarise or re-title it.
 *
 * **THE COUNTS ARE THE DATABASE'S ARITHMETIC**, computed by the capability that
 * fetched the Project, and shown as themselves. The model has no field in which
 * to contradict them, and this component does no arithmetic of its own.
 */

function statusLabel(status: string): string {
  // The server types this loosely (`string`) because a card is generic across
  // Sol's source domains. Anything outside the locked Project vocabulary is
  // shown as nothing rather than as a raw value — the same rule the Capture
  // card applies to Capture statuses.
  const known: ProjectStatus[] = ['ongoing', 'finished', 'closed'];
  const match = known.find((candidate) => candidate === status);
  return match ? PROJECT_STATUS_LABEL[match] : '';
}

export interface SolProjectSourceCardLinkProps {
  readonly card: SolProjectSourceCard;
  /**
   * The CANONICAL Crewlink timezone from the profile — never the device's, and
   * never defaulted (INV-T17). Null renders no timestamp rather than a wrong one.
   */
  readonly timeZone: string | null;
}

export function SolProjectSourceCardLink({ card, timeZone }: SolProjectSourceCardLinkProps) {
  const name = projectLabel(card.project.name);
  const status = statusLabel(card.status);
  const created = timeZone ? formatInstant(card.createdAt, timeZone) : '';
  const counts = card.counts;

  return (
    <Link
      className="sol-source sol-source--project"
      to={`/projects/${card.source.id}`}
      aria-label={`Open Project ${name}`}
    >
      <span className="sol-source-head">
        <span className="sol-source-title">{name}</span>
        {status ? <span className="sol-source-status">{status}</span> : null}
      </span>
      <span className="sol-source-meta">
        {card.isCurrent ? 'The Project you are in' : 'Project'}
        {created ? ` · Started ${created}` : ''}
      </span>
      {counts ? (
        // Crewlink's own words for its own statuses, in the locked order.
        <span className="sol-source-caption">
          {`${counts.open} open · ${counts.repaired} repaired · ${counts.closed} closed`}
        </span>
      ) : null}
      <span className="sol-source-open" aria-hidden="true">
        Open Project →
      </span>
    </Link>
  );
}
